import { useRef, useState } from "react";
import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FormErrors } from "@/components/forms/form-errors";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { money } from "../jobs/types";
import {
  locationJobPath,
  locationJobFiscalDocumentsPath,
  newLocationJobFiscalDocumentPath,
  editLocationJobFiscalDocumentPath,
  locationJobFiscalDocumentIssuancePath,
  locationJobFiscalDocumentRefreshPath,
  locationJobFiscalDocumentPaymentAllocationsPath,
} from "@/routes";

type Document = {
  id: string;
  dispatch_state: string | null;
  dispatch_error: string | null;
  revision: string;
  state: string;
  kind: string;
  outdated_review: boolean;
  provider: string | null;
  provider_account: string | null;
  environment: string | null;
  document_context: string;
  reporting_context: string;
  provider_document_id: string | null;
  provider_number: string | null;
  delivery_state: string;
  reporting_state: string;
  credited_minor: string;
  credit_balance_minor: string;
  allocations: {
    id: string;
    amount_minor: string;
    reference: string;
    recorded_at: string;
  }[];
  allocated_minor: string;
  outstanding_minor: string;
  allocation_key: string;
  postings: { id: string; reference: string; available_minor: string }[];
  snapshot: {
    issuer: Record<string, string>;
    customer: Record<string, string>;
    advance_minor: string;
    amount_due_minor: string;
    total: {
      currency: string;
      net_minor: string;
      tax_minor: string;
      total_minor: string;
      job_discount_minor: string;
      item_discount_minor: string;
      lines: {
        line_id: string;
        name: string;
        quantity: number;
        tax_label: string;
        tax_mode: string;
        tax_rate_parts_per_million: number;
        net_minor: string;
        tax_minor: string;
        total_minor: string;
      }[];
    };
  };
};
export default function FiscalIndex({
  job_id,
  selected_document_id,
  financial,
  ready,
  documents,
  pagination,
}: {
  job_id: string;
  selected_document_id?: string | null;
  financial: boolean;
  ready: boolean;
  documents: Document[];
  pagination: { page: number; pages: number };
}) {
  const { auth, i18n, errors } = usePage().props;
  const [selected, setSelected] = useState<string | null>(
    selected_document_id || null,
  );
  const trigger = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location.id;
  const selectedDocument = documents.find(
    (document) => document.id === selected,
  );
  return (
    <JobWorkspace
      primaryAction={
        ready && documents.length === 0 ? (
          <Button
            render={
              <Link href={newLocationJobFiscalDocumentPath(location, job_id)} />
            }
          >
            {t("fiscal_documents.prepare")}
          </Button>
        ) : (
          false
        )
      }
      breadcrumbs={[
        {
          title: t("fiscal_documents.title"),
          href: locationJobFiscalDocumentsPath(location, job_id),
        },
      ]}
    >
      <Head title={t("fiscal_documents.title")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">
          {t("fiscal_documents.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("fiscal_documents.independent_help")}
        </p>
        {!selectedDocument && <FormErrors errors={errors || {}} />}
        {!ready && <p role="status">{t("fiscal_documents.review_required")}</p>}
        {documents.length === 0 && <p>{t("fiscal_documents.empty")}</p>}
        {documents.map((document) => {
          const total = document.snapshot.total;
          const amount = (value: string) =>
            money(value, total.currency, i18n.locale);
          return (
            <Card key={document.id} data-fiscal-state={document.state}>
              <CardHeader>
                <CardTitle>
                  {t(`fiscal_documents.${document.kind}`)} ·{" "}
                  {t(`fiscal_documents.${document.state}`)}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 pb-5 sm:grid-cols-[minmax(0,1fr)_auto]">
                <div className="min-w-0 space-y-2">
                  <p className="font-medium">
                    {document.provider_number || t("fiscal_documents.draft")} ·{" "}
                    {amount(total.total_minor)}
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    <span>
                      {t("fiscal_documents.outstanding_minor")}:{" "}
                      <span data-fiscal-money="outstanding">
                        {amount(document.outstanding_minor)}
                      </span>
                    </span>
                    <span>
                      {t("fiscal_documents.delivery")}:{" "}
                      {t(`fiscal_documents.${document.delivery_state}`)}
                    </span>
                    <span>
                      {t("fiscal_documents.reporting")}:{" "}
                      {t(
                        `fiscal_documents.${document.reporting_state === "rejected" ? "rejected_status" : document.reporting_state}`,
                      )}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={(event) => {
                    trigger.current = event.currentTarget;
                    setSelected(document.id);
                  }}
                >
                  {t("workflow.document_details")}
                </Button>
              </CardContent>
              <Sheet
                open={selected === document.id}
                onOpenChange={(open) => {
                  if (!open) setSelected(null);
                }}
              >
                <SheetContent
                  className="w-full! overflow-y-auto sm:max-w-2xl!"
                  finalFocus={trigger}
                >
                  <SheetHeader>
                    <SheetTitle>
                      {t(`fiscal_documents.${document.kind}`)} ·{" "}
                      {document.provider_number ||
                        t(`fiscal_documents.${document.state}`)}
                    </SheetTitle>
                    <SheetDescription>
                      {t("workflow.document_help")}
                    </SheetDescription>
                  </SheetHeader>
                  <div className="space-y-5 px-4 pb-4 break-words">
                    <FormErrors
                      errors={errors || {}}
                      targets={{ amount: `allocation-${document.id}` }}
                    />
                    {document.provider ? (
                      <p>
                        {t(`fiscal_documents.${document.provider}`)} ·{" "}
                        {t(`fiscal_documents.${document.environment}`)}
                      </p>
                    ) : (
                      <p role="status">
                        {t("fiscal_documents.connection_pending")}
                      </p>
                    )}
                    {document.dispatch_state &&
                      document.dispatch_state !== "completed" && (
                        <p role="alert">
                          {t(
                            `integrations.dispatch.${document.dispatch_state}`,
                          )}
                        </p>
                      )}
                    {document.provider_document_id && (
                      <p>
                        {document.provider_number} ·{" "}
                        {document.provider_document_id}
                      </p>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                      {["issuer", "customer"].map((party) => (
                        <section key={party}>
                          <h2 className="font-medium">
                            {t(`fiscal_documents.${party}`)}
                          </h2>
                          {Object.entries(
                            document.snapshot[party as "issuer" | "customer"],
                          ).map(
                            ([field, value]) =>
                              value && (
                                <p key={field}>
                                  {t(`fiscal_documents.${field}`)}: {value}
                                </p>
                              ),
                          )}
                        </section>
                      ))}
                    </div>
                    <ul className="space-y-3">
                      {total.lines.map((line) => (
                        <li
                          key={line.line_id}
                          className="rounded-lg border p-3"
                        >
                          <p className="font-medium">
                            {line.name} × {line.quantity}
                          </p>
                          <p>
                            {line.tax_label} ·{" "}
                            {t(`fiscal_documents.${line.tax_mode}`)} ·{" "}
                            {line.tax_rate_parts_per_million / 10000}%
                          </p>
                          <p>
                            {t("fiscal_documents.net_minor")}:{" "}
                            {amount(line.net_minor)} ·{" "}
                            {t("fiscal_documents.tax_minor")}:{" "}
                            {amount(line.tax_minor)} ·{" "}
                            {amount(line.total_minor)}
                          </p>
                        </li>
                      ))}
                    </ul>
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        "net_minor",
                        "tax_minor",
                        "item_discount_minor",
                        "job_discount_minor",
                        "total_minor",
                      ].map((field) => (
                        <div key={field}>
                          <dt className="text-muted-foreground text-sm">
                            {t(`fiscal_documents.${field}`)}
                          </dt>
                          <dd>{amount(total[field as "net_minor"])}</dd>
                        </div>
                      ))}
                      <div>
                        <dt>{t("fiscal_documents.advance_minor")}</dt>
                        <dd>{amount(document.snapshot.advance_minor)}</dd>
                      </div>
                      <div>
                        <dt>{t("fiscal_documents.amount_due_minor")}</dt>
                        <dd>{amount(document.snapshot.amount_due_minor)}</dd>
                      </div>
                      <div>
                        <dt>{t("fiscal_documents.allocated_minor")}</dt>
                        <dd data-detail-money="allocated">
                          {amount(document.allocated_minor)}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("fiscal_documents.outstanding_minor")}</dt>
                        <dd data-fiscal-money="outstanding">
                          {amount(document.outstanding_minor)}
                        </dd>
                      </div>
                      {BigInt(document.credited_minor) > 0n && (
                        <div>
                          <dt>{t("fiscal_documents.credited_minor")}</dt>
                          <dd>{amount(document.credited_minor)}</dd>
                        </div>
                      )}
                      {BigInt(document.credit_balance_minor) > 0n && (
                        <div>
                          <dt>{t("fiscal_documents.credit_balance_minor")}</dt>
                          <dd>{amount(document.credit_balance_minor)}</dd>
                        </div>
                      )}
                      <div>
                        <dt>{t("fiscal_documents.delivery")}</dt>
                        <dd>
                          {t(`fiscal_documents.${document.delivery_state}`)}
                        </dd>
                      </div>
                      <div>
                        <dt>{t("fiscal_documents.reporting")}</dt>
                        <dd>
                          {t(
                            `fiscal_documents.${document.reporting_state === "rejected" ? "rejected_status" : document.reporting_state}`,
                          )}
                        </dd>
                      </div>
                    </dl>
                    {document.allocations.length > 0 && (
                      <section className="space-y-3">
                        <h2 className="font-medium">
                          {t("fiscal_documents.allocation_history")}
                        </h2>
                        <ul className="space-y-2">
                          {document.allocations.map((item) => (
                            <li key={item.id}>
                              {item.reference} · {amount(item.amount_minor)}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    {document.outdated_review &&
                      document.kind === "invoice" && (
                        <Form
                          action={locationJobFiscalDocumentRefreshPath(
                            location,
                            job_id,
                            document.id,
                            { return_page: pagination.page },
                          )}
                          method="post"
                        >
                          {({ processing }) => (
                            <div className="space-y-3">
                              <p role="alert">
                                {t("fiscal_documents.review_changed")}
                              </p>
                              <input
                                type="hidden"
                                name="return_page"
                                value={pagination.page}
                              />
                              <input
                                type="hidden"
                                name="revision"
                                value={document.revision}
                              />
                              <Label className="flex items-start gap-2">
                                <Checkbox
                                  name="confirmed"
                                  value="1"
                                  required
                                  aria-label={t(
                                    "fiscal_documents.refresh_confirm",
                                  )}
                                />
                                {t("fiscal_documents.refresh_confirm")}
                              </Label>
                              <Button type="submit" disabled={processing}>
                                {t("fiscal_documents.refresh")}
                              </Button>
                            </div>
                          )}
                        </Form>
                      )}
                    {document.state === "draft" &&
                      document.kind === "invoice" && (
                        <Link
                          className="block underline"
                          href={editLocationJobFiscalDocumentPath(
                            location,
                            job_id,
                            document.id,
                            { return_page: pagination.page },
                          )}
                        >
                          {t("fiscal_documents.edit")}
                        </Link>
                      )}
                    {financial && document.state !== "issued" && (
                      <Form
                        action={locationJobFiscalDocumentIssuancePath(
                          location,
                          job_id,
                          document.id,
                        )}
                        method="post"
                      >
                        {({ processing }) => (
                          <div className="space-y-3">
                            <input
                              type="hidden"
                              name="return_page"
                              value={pagination.page}
                            />
                            <input
                              type="hidden"
                              name="revision"
                              value={document.revision}
                            />
                            <p className="text-muted-foreground text-sm">
                              {t("fiscal_documents.unavailable")}
                            </p>
                            <Label className="flex items-start gap-2">
                              <Checkbox
                                name="confirmed"
                                value="1"
                                required
                                aria-label={t("fiscal_documents.confirm")}
                              />
                              {t("fiscal_documents.confirm")}
                            </Label>
                            <Button type="submit" disabled={processing}>
                              {t("fiscal_documents.request")}
                            </Button>
                          </div>
                        )}
                      </Form>
                    )}
                    {financial &&
                      document.state === "issued" &&
                      document.kind === "invoice" &&
                      document.postings.length > 0 &&
                      BigInt(document.outstanding_minor) > 0n && (
                        <Form
                          action={locationJobFiscalDocumentPaymentAllocationsPath(
                            location,
                            job_id,
                            document.id,
                            { return_page: pagination.page },
                          )}
                          method="post"
                        >
                          {({ processing }) => (
                            <div className="space-y-3">
                              <h2 className="font-medium">
                                {t("fiscal_documents.allocate")}
                              </h2>
                              <p className="text-muted-foreground text-sm">
                                {t("fiscal_documents.allocation_help")}
                              </p>
                              <input
                                type="hidden"
                                name="return_page"
                                value={pagination.page}
                              />
                              <input
                                type="hidden"
                                name="payment_allocation[entry_key]"
                                value={document.allocation_key}
                              />
                              <Label htmlFor={`posting-${document.id}`}>
                                {t("fiscal_documents.receipt")}
                              </Label>
                              <Select
                                name="payment_allocation[payment_posting_id]"
                                defaultValue={document.postings[0].id}
                                items={Object.fromEntries(
                                  document.postings.map((item) => [
                                    item.id,
                                    `${item.reference} · ${amount(item.available_minor)}`,
                                  ]),
                                )}
                              >
                                <SelectTrigger id={`posting-${document.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {document.postings.map((item) => (
                                    <SelectItem key={item.id} value={item.id}>
                                      {item.reference} ·{" "}
                                      {amount(item.available_minor)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Label htmlFor={`allocation-${document.id}`}>
                                {t("payments.amount")} ({total.currency})
                              </Label>
                              <Input
                                id={`allocation-${document.id}`}
                                name="payment_allocation[amount]"
                                required
                                inputMode="decimal"
                                maxLength={25}
                              />
                              <Button type="submit" disabled={processing}>
                                {t("fiscal_documents.allocate")}
                              </Button>
                            </div>
                          )}
                        </Form>
                      )}
                  </div>
                </SheetContent>
              </Sheet>
            </Card>
          );
        })}
        <nav
          className="flex flex-wrap gap-4"
          aria-label={t("fiscal_documents.title")}
        >
          {pagination.page > 1 && (
            <Link
              className="underline"
              href={locationJobFiscalDocumentsPath(location, job_id, {
                page: pagination.page - 1,
              })}
            >
              {t("customers.previous")}
            </Link>
          )}
          {pagination.page < pagination.pages && (
            <Link
              className="underline"
              href={locationJobFiscalDocumentsPath(location, job_id, {
                page: pagination.page + 1,
              })}
            >
              {t("customers.next")}
            </Link>
          )}
        </nav>
        <Link
          className="block underline"
          href={locationJobPath(location, job_id)}
        >
          {t("jobs.title")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
